import React from 'react';
import { FormattedMessage } from 'react-intl';

import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import styles from './styles.module.less';
import { useDdpCountryConfig } from '../../hooks/use-ddp-country-config';
import { CostReviewLabelProviderInsurance } from '../cost-review-line-item/cost-review-label-provider-insurance/cost-review-label-provider-insurance';
import { CostReviewLineItemDuties } from '../cost-review-line-item/cost-review-line-item-duties';
import CostReviewLineItemService from '../cost-review-line-item/cost-review-line-item-service';
import { CostReviewLineItemShippingService } from '../cost-review-line-item/cost-review-line-item-shipping-service/cost-review-line-item-shipping-service';
import { CostReviewLineItemVAT } from '../cost-review-line-item/cost-review-line-item-vat';
import { messages } from '../cost-review-line-item/messages';
import { useCostReviewLabelProviderLineItems } from '../cost-review-line-item/use-cost-review-label-provider-line-items';
import GetProviderDisplayName from '../data-driven-logo/get-provider-display-name';
import { AdditionalChargesCollapsible } from '../modals/label-batch-status/cost-review/additional-charges-collapsible';

/**
 * @typedef {import('../../prop-types/cost-review.types').LabelProviderCostReview} LabelProviderCostReview
 * @typedef {import('../../prop-types/cost-review.types').CostReviewFromProviderShippingServiceRecommendationMap} CostReviewFromProviderShippingServiceRecommendationMap
 * @typedef {import('../../prop-types/cost-review.types').CostReviewSelectedRecommendations} CostReviewSelectedRecommendations
 * @typedef {import('../../prop-types/cost-review.types').CostReviewInsuranceRecommendation} CostReviewInsuranceRecommendation
 * @typedef {import('../../prop-types/cost-review.types').CostReviewFromProviderRateShopperRecommendationMap} CostReviewFromProviderRateShopperRecommendationMap
 */

/**
 * @param {object} props
 * @param {LabelProviderCostReview} props.providerCost
 * @param {CostReviewFromProviderShippingServiceRecommendationMap} [props.shippingServiceRecommendations]
 * @param {(recommendationKey: string) => void} [props.onToggleShippingServiceRecommendation]
 * @param {boolean} [props.isToggleRecommendationDisabled]
 * @param {CostReviewSelectedRecommendations} [props.selectedShippingServiceRecommendations]
 * @param {CostReviewInsuranceRecommendation} [props.insuranceRecommendation]
 * @param {(recommendationKey: string) => void} [props.toggleInsuranceRecommendation]
 * @param {boolean} [props.isInsuranceRecommendationSelected]
 * @param {CostReviewFromProviderRateShopperRecommendationMap} [props.rateShopperRecommendations]
 * @param {CostReviewSelectedRecommendations} [props.selectedRateShopperRecommendations]
 * @param {(recommendationKey: string) => void} [props.onToggleRateShopperRecommendation]
 * @returns {React.JSX.Element}
 */
export const WalletedCostReviewProviderGroup = ({
  providerCost = emptyObject,
  shippingServiceRecommendations = emptyObject,
  onToggleShippingServiceRecommendation = noop,
  isToggleRecommendationDisabled = false,
  selectedShippingServiceRecommendations = emptyObject,
  insuranceRecommendation,
  isInsuranceRecommendationSelected,
  toggleInsuranceRecommendation,
  rateShopperRecommendations = emptyObject,
  selectedRateShopperRecommendations = emptyObject,
  onToggleRateShopperRecommendation = noop,
}) => {
  const { isDdpAvailable: isPrepaidDutiesAndTaxEnabled } = useDdpCountryConfig();
  const {
    services,
    lineItems,
    providerInsuranceSubtotal = 0,
    providerConfirmationSubtotal = 0,
    providerOtherCostSubtotal = 0,
    labelProviderId,
  } = providerCost;

  const isWallet = true;

  const {
    additionalChargesLineItems,
    insuranceLineItems,
    areAdditionalChargesProvided,
    shouldShowConfirmationSubtotal,
    shouldShowOtherCostsSubtotal,
    shouldShowInsurance,
    VATTaxChargeLineItem,
    DutiesLineItem,
  } = useCostReviewLabelProviderLineItems(
    lineItems,
    providerInsuranceSubtotal,
    providerConfirmationSubtotal,
    providerOtherCostSubtotal,
    isWallet,
  );

  const providerName = GetProviderDisplayName(labelProviderId);
  const displayName = typeof providerName === 'string' ? providerName : <FormattedMessage {...providerName} />;

  return (
    <>
      <div className={styles['walleted-provider-header-line']}>{displayName}</div>
      {services.map((service) => (
        <CostReviewLineItemShippingService
          key={service.shippingServiceId}
          isToggleRecommendationDisabled={isToggleRecommendationDisabled}
          onToggleRateShopperRecommendation={onToggleRateShopperRecommendation}
          onToggleRecommendation={onToggleShippingServiceRecommendation}
          rateShopperRecommendations={rateShopperRecommendations[service.shippingServiceId]}
          selectedRateShopperRecommendations={selectedRateShopperRecommendations}
          selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
          service={service}
          shippingServiceRecommendations={shippingServiceRecommendations[service.shippingServiceId]}
        />
      ))}
      {shouldShowInsurance ? (
        <CostReviewLabelProviderInsurance
          insuranceLineItems={insuranceLineItems}
          insuranceRecommendation={insuranceRecommendation}
          insuranceSubtotal={providerInsuranceSubtotal}
          isRecommendationSelected={isInsuranceRecommendationSelected}
          isToggleRecommendationDisabled={isToggleRecommendationDisabled}
          onToggleRecommendation={toggleInsuranceRecommendation}
        />
      ) : null}
      {shouldShowConfirmationSubtotal ? (
        <CostReviewLineItemService
          serviceCost={providerConfirmationSubtotal}
          serviceName={<FormattedMessage {...messages.confirmation} />}
        />
      ) : null}
      {shouldShowOtherCostsSubtotal ? (
        <CostReviewLineItemService
          serviceCost={providerOtherCostSubtotal}
          serviceName={<FormattedMessage {...messages.additionalCharges} />}
        />
      ) : null}
      {areAdditionalChargesProvided ? <AdditionalChargesCollapsible lineItems={additionalChargesLineItems} /> : null}

      <CostReviewLineItemVAT
        currencyCode={VATTaxChargeLineItem?.amount.code}
        percentage={VATTaxChargeLineItem?.percentage}
        value={VATTaxChargeLineItem?.amount.value}
        description={VATTaxChargeLineItem?.description}
      />
      {isPrepaidDutiesAndTaxEnabled ? (
        <CostReviewLineItemDuties currencyCode={DutiesLineItem?.amount.code} isWalleted={isWallet} value={DutiesLineItem?.amount.value} />
      ) : null}
    </>
  );
};
